import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface DeletionLog {
  id: number;
  user_id: string;
  deleted_count: number;
  method: "auto" | "manual";
  deleted_at: string;
}

export default function RecentDeletionLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<DeletionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        setLogs([]);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke("get-deletion-logs", {
        body: { user_id: userId },
      });

      if (fnError) {
        console.error("[RecentDeletionLogs] Edge Function error:", fnError);
        setError("불러오기 실패");
        return;
      }

      if (data?.error) {
        console.error("[RecentDeletionLogs] data error:", data.error);
        setError("불러오기 실패");
      } else {
        setLogs((data?.logs ?? []).slice(0, 5));
      }
    } catch (err) {
      console.error("[RecentDeletionLogs] fetch exception:", err);
      setError("불러오기 실패");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getRelativeTime = (dateStr: string): string => {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return "방금 전";
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;
    return new Date(dateStr).toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
  };

  const totalDeleted = logs.reduce((s, l) => s + l.deleted_count, 0);

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-6 flex flex-col">
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <div>
          <h3 className="text-white font-semibold text-sm md:text-base">최근 삭제 내역</h3>
          <p className="text-gray-500 text-xs mt-0.5">자동/수동 삭제 요약</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors rounded-md"
            title="새로고침"
          >
            <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`}></i>
          </button>
          <button
            onClick={() => navigate("/dashboard/deletion-logs")}
            className="text-xs text-violet-400 hover:text-violet-300 font-medium cursor-pointer whitespace-nowrap transition-colors"
          >
            전체보기 <i className="ri-arrow-right-line text-[10px] ml-0.5"></i>
          </button>
        </div>
      </div>

      <div className="flex-1">
        {loading ? (
          <div className="py-8 text-center">
            <div className="w-5 h-5 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-gray-500 text-xs">불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="py-8 text-center">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-red-500/10">
              <i className="ri-error-warning-line text-red-400 text-lg"></i>
            </div>
            <p className="text-red-400 text-sm font-medium mb-1">{error}</p>
            <button
              onClick={fetchLogs}
              className="text-violet-400 hover:text-violet-300 text-xs cursor-pointer transition-colors"
            >
              다시 시도
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-white/[0.04]">
              <i className="ri-delete-bin-6-line text-gray-600 text-xl"></i>
            </div>
            <p className="text-gray-500 text-sm">삭제 내역이 없습니다</p>
            <p className="text-gray-600 text-xs mt-0.5">아직 삭제된 발송 건이 없어요</p>
          </div>
        ) : (
          <div className="space-y-2 md:space-y-2.5">
            {logs.map((log) => (
              <div
                key={log.id}
                className="flex items-center gap-2 md:gap-3 p-2 md:p-2.5 hover:bg-white/[0.03] rounded-xl transition-colors group"
              >
                {/* Method icon */}
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
                    log.method === "auto"
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-amber-500/15 text-amber-400"
                  }`}
                >
                  <i className={`${log.method === "auto" ? "ri-timer-line" : "ri-user-line"} text-sm`}></i>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap ${
                        log.method === "auto"
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-amber-500/15 text-amber-400"
                      }`}
                    >
                      {log.method === "auto" ? "자동" : "수동"}
                    </span>
                    <span className="text-white text-xs md:text-sm font-semibold whitespace-nowrap">
                      {log.deleted_count.toLocaleString()}건 삭제
                    </span>
                  </div>
                </div>

                {/* Time */}
                <span className="text-gray-600 text-[10px] md:text-xs whitespace-nowrap shrink-0">
                  {getRelativeTime(log.deleted_at)}
                </span>
              </div>
            ))}

            {/* Summary footer */}
            {totalDeleted > 0 && (
              <div className="pt-2 mt-1 border-t border-white/[0.06]">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs">최근 {logs.length}건 합계</span>
                  <span className="text-gray-300 text-xs font-semibold">
                    총 {totalDeleted.toLocaleString()}건 삭제됨
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}